import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, ADMIN_ROLES, ROUTES } from '../config/authConfig';
import { FaceAuthResponse, FaceAuthHookResult } from '../types/authTypes';
import { clearUserSession } from '../utils/sessionManager';
import { CameraCaptureRef } from '../Components/CameraCapture';

export const useFaceAuth = (): FaceAuthHookResult => {
  const [faceIdLoading, setFaceIdLoading] = useState<boolean>(false);
  const [faceIdError, setFaceIdError] = useState<string | null>(null);
  const [showFaceIdDialog, setShowFaceIdDialog] = useState<boolean>(false);
  const cameraRef = useRef<CameraCaptureRef>(null);
  const navigate = useNavigate();

  const handleFaceIdLogin = () => {
    setFaceIdError(null);
    setShowFaceIdDialog(true);
  };

  const handleFaceCapture = async (blob: Blob) => {
    console.log('[FACE_AUTH] Получено фото, размер:', blob.size);
    setFaceIdLoading(true);
    setFaceIdError(null);

    const formData = new FormData();
    formData.append('file', blob, 'photo.jpg');

    try {
      console.log('[FACE_AUTH] Отправка фото на Python сервер...');
      const response = await fetch(API_CONFIG.FACE_ID_URL, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FaceAuthResponse = await response.json();
      console.log('[FACE_AUTH] Ответ от Python сервера:', result);

      // Проверяем, что получили данные пользователя
      if (result.success && result.user) {
        console.log('[FACE_AUTH] Пользователь распознан:', result.user.username);
        
        // Проверяем, не деактивирован ли пользователь
        if (result.user.deactivation === 1) {
          setFaceIdError('Учетная запись приостановлена');
          return;
        }

        // Clear any leftover data from a previous session
        clearUserSession();

        // Сохраняем данные пользователя напрямую
        localStorage.setItem('userId', result.user.id.toString());
        localStorage.setItem('role', result.user.role);
        localStorage.setItem('username', result.user.username);
        localStorage.setItem('guid', result.user.guid || '');

        console.log('[FACE_AUTH] Авторизация успешна для пользователя:', result.user.username);

        // Закрываем диалог
        setShowFaceIdDialog(false);

        // Перенаправляем пользователя в зависимости от роли
        if (ADMIN_ROLES.includes(result.user.role as any)) {
          navigate(ROUTES.ADMIN);
        } else {
          navigate(ROUTES.DASHBOARD);
        }
      } else {
        console.error('[FACE_AUTH] Неверный ответ от сервера:', result);
        setFaceIdError(result.message || 'Лицо не распознано или не найдено в базе данных');
      }
    } catch (error) {
      console.error('[FACE_AUTH] Ошибка при распознавании лица:', error);
      setFaceIdError('Ошибка подключения к серверу распознавания лиц');
    } finally {
      setFaceIdLoading(false);
    }
  };

  const handleCloseFaceIdDialog = () => {
    setShowFaceIdDialog(false);
    setFaceIdError(null);
    setFaceIdLoading(false);
    cameraRef.current?.stopCamera();
  };

  return {
    faceIdLoading,
    faceIdError,
    showFaceIdDialog,
    handleFaceIdLogin,
    handleFaceCapture,
    handleCloseFaceIdDialog,
    cameraRef
  } as FaceAuthHookResult & { cameraRef: React.RefObject<CameraCaptureRef> };
};
